import {IntlShape} from 'react-intl';
import {
  PROD_STRIPE_JS_PUBLISHABLE_KEY,
  DEVBOX_STRIPE_JS_PUBLISHABLE_KEY,
} from './constants';

export type ServiceEnvironment =
  | 'remote-devbox'
  | 'devbox'
  | 'isolated_qa'
  | 'qa'
  | 'preprod'
  | 'prod';

export const getAmountErrors = (
  amount: string | null | undefined,
  intl: IntlShape,
  errorMessage: {id: string; defaultMessage: string; description: string},
): Array<string> | null => {
  const errorMsg = [intl.formatMessage(errorMessage)];

  // Check if amount is null or undefined
  if (!amount) {
    return errorMsg;
  }

  // Check for the presence of the dollar sign
  if (amount.includes('$')) {
    return errorMsg;
  }

  const parsedAmount = amount.trim();
  const cents = parsedAmount.split('.')[1];

  const validCurrencyRegex = /^\d+(\.\d{0,2})?$/;

  if (
    !validCurrencyRegex.test(parsedAmount) ||
    isNaN(parseFloat(parsedAmount)) ||
    parseFloat(parsedAmount) <= 0 ||
    parseFloat(parsedAmount) > 2 ** 63 - 1 || // Elastic Search Long has range +/- 2^63 - 1
    (parsedAmount[0] === '0' && parsedAmount[1] !== '.') ||
    ((cents || parsedAmount.includes('.')) && cents.length < 2)
  ) {
    return errorMsg;
  } else {
    return null;
  }
};

// Validation function for individual date components (day, month, year)
export const getDateErrors = (
  day: string,
  month: string,
  year: string,
  intl: IntlShape,
  errorMessage: {id: string; defaultMessage: string; description: string},
): Array<string> | null => {
  // Check if any of the fields are empty
  if (!day || !month || !year) {
    return [intl.formatMessage(errorMessage)];
  }

  // Create a date string and validate it
  const dateString = `${year}-${month.padStart(2, '0')}-${day.padStart(
    2,
    '0',
  )}`;
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return [intl.formatMessage(errorMessage)];
  }

  // to prevent silent date rollovers (like prevent 30th feb from being accepted)
  if (
    date.getFullYear() !== parseInt(year, 10) ||
    date.getMonth() + 1 !== parseInt(month, 10) ||
    date.getDate() !== parseInt(day, 10)
  ) {
    return [intl.formatMessage(errorMessage)];
  }

  // to prevent future dates from being accepted
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  if (date > today) {
    return [intl.formatMessage(errorMessage)];
  }

  return null;
};

const BASE_10_RADIX = 10;

export const inCents = (amount: string): number => {
  const trimmedAmount = amount.trim();
  let resultCents = 0;
  if (trimmedAmount.includes('.')) {
    const [dollars, cents] = trimmedAmount.split('.');
    resultCents += parseInt(dollars, BASE_10_RADIX) * 100;
    if (cents.length === 2) {
      resultCents += parseInt(cents, BASE_10_RADIX);
    } else {
      return resultCents;
    }
  } else {
    resultCents += parseInt(trimmedAmount, BASE_10_RADIX) * 100;
  }
  return resultCents;
};

// helper function to decipher the environment and retrieve the correct key
export const getServiceEnvironment = (): ServiceEnvironment => {
  const url = new URL(window.location.href);
  const origin = url.host.toLowerCase();

  if (
    origin.startsWith('localhost') ||
    origin.endsWith('-mydev.dev.stripe.me')
  ) {
    return 'devbox';
  } else if (origin.endsWith('.dev.stripe.me')) {
    return 'isolated_qa';
  } else if (origin.startsWith('qa-')) {
    return 'qa';
  } else if (origin.startsWith('preprod-')) {
    return 'preprod';
  }

  return 'prod';
};

export const getStripePublishableKey = (): string => {
  const environment = getServiceEnvironment();

  if (environment === 'prod' || environment === 'preprod') {
    return PROD_STRIPE_JS_PUBLISHABLE_KEY;
  }
  return DEVBOX_STRIPE_JS_PUBLISHABLE_KEY;
};
